import { Link } from 'react-router-dom';
import { accountStatus } from './balance';
import { formatMoney, usd } from './pnl';
import { FreshnessNote } from './AccountStatus';
import { useI18n } from './i18n';

// Accounts with rules worth watching during a session.
// `selected` is 'all', one account id or a list of ids.
function watched(accounts, selected) {
  const ids = selected === 'all' ? null : Array.isArray(selected) ? selected : [selected];
  const list = ids ? accounts.filter((a) => ids.includes(a._id)) : accounts.filter((a) => !a.archived);
  return list.filter((a) => a.size && (a.maxLossLimit || a.dailyLossLimit));
}

function Meter({ used, risk, label }) {
  const pct = Math.max(0, Math.min(1, used)) * 100;
  return (
    <span className="risk-meter-bar" role="img" aria-label={label}>
      <span className={`risk-meter-fill risk-${risk}`} style={{ width: `${pct}%` }} />
    </span>
  );
}

// Today's P&L against the daily loss limit and the max loss, for the live session.
// Built only from recorded trades, and labeled that way.
export default function RiskMeter({ accounts, trades, selected }) {
  const { t } = useI18n();
  const list = watched(accounts, selected);
  if (!list.length) {
    return (
      <section className="risk-panel risk-panel-empty">
        <span className="routine-block-title">{t('Your risk today')}</span>
        <p className="sidebar-muted">
          {t('Add a starting balance and your daily loss limit or max loss to an account to see how much room you have left today.')}{' '}
          <Link to="/app">{t('Set up account rules')} &rarr;</Link>
        </p>
      </section>
    );
  }
  const ids = new Set(list.map((a) => a._id));
  const shownTrades = trades.filter((x) => ids.has(x.accountId));
  return (
    <section className="risk-panel" aria-label={t('Your risk today')}>
      <span className="routine-block-title">{t('Your risk today')}</span>
      <ul className="risk-accounts">
        {list.map((a) => {
          const s = accountStatus(a, trades.filter((x) => x.accountId === a._id));
          const lostToday = Math.max(0, -s.todayNet);
          return (
            <li key={a._id} className="risk-account">
              <div className="risk-account-head">
                <strong>{a.name}</strong>
                <span>{t('Today')} <b className={s.todayNet > 0 ? 'gain' : s.todayNet < 0 ? 'loss' : ''}>{formatMoney(s.todayNet)}</b></span>
              </div>
              {a.dailyLossLimit > 0 && (
                <div className="risk-line">
                  <div className="risk-line-head">
                    <span>{t('Daily loss limit')}</span>
                    <span className={`risk-text-${s.dailyRisk}`}>
                      {s.dailyLeft <= 0
                        ? t('Limit hit: stop trading today')
                        : t('{left} left of {limit}', { left: usd.format(s.dailyLeft), limit: usd.format(a.dailyLossLimit) })}
                    </span>
                  </div>
                  <Meter used={lostToday / a.dailyLossLimit} risk={s.dailyRisk}
                    label={t('{pct}% of today’s loss limit used', { pct: Math.round((lostToday / a.dailyLossLimit) * 100) })} />
                </div>
              )}
              {s.toBlow !== undefined && (
                <div className="risk-line">
                  <div className="risk-line-head">
                    <span>{t('Max loss')}</span>
                    <span className={`risk-text-${s.risk}`}>
                      {s.risk === 'blown' ? t('Max loss hit') : t('{amount} from max loss', { amount: usd.format(s.toBlow) })}
                    </span>
                  </div>
                  <Meter used={1 - s.toBlow / a.maxLossLimit} risk={s.risk}
                    label={t('{amount} above max loss level', { amount: usd.format(Math.max(0, s.toBlow)) })} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <FreshnessNote trades={shownTrades} />
    </section>
  );
}
